import { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, ChevronRight, ChevronDown, Copy, Download, ExternalLink, FileText, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { MarkdownContent } from '@/components/markdown-content'
import type { ChatMessage } from './chat-api'
import { SkillInvocationModal } from './skill-invocation-modal'
import type { AttachmentRef } from './attachments-api'
import { AttachmentPill } from './attachment-pill'
import { AttachmentPreviewModal } from './attachment-preview-modal'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authenticatedOutputUrl, useOutputAuthToken } from '@/features/output/output-url'
import { usePutVaultFile } from '@/features/vault/use-vault'
import { ReadAloud } from './read-aloud'

const eurFormatter = new Intl.NumberFormat('de-CH', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

const relativeTimeFormatter = new Intl.RelativeTimeFormat('de-CH', { numeric: 'auto' })

/** Relative Zeit auf Deutsch ("vor 3 Tagen", "gestern") fuer Quellen-Chips. */
function formatRelativeDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const minutes = Math.round((date.getTime() - Date.now()) / 60_000)
  if (Math.abs(minutes) < 1) return relativeTimeFormatter.format(0, 'second')
  if (Math.abs(minutes) < 60) return relativeTimeFormatter.format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return relativeTimeFormatter.format(hours, 'hour')
  const days = Math.round(hours / 24)
  if (Math.abs(days) < 30) return relativeTimeFormatter.format(days, 'day')
  const months = Math.round(days / 30)
  if (Math.abs(months) < 12) return relativeTimeFormatter.format(months, 'month')
  return relativeTimeFormatter.format(Math.round(months / 12), 'year')
}

interface MessageBubbleProps {
  message: ChatMessage
  streaming?: boolean
}

export function MessageBubble({ message, streaming = false }: MessageBubbleProps) {
  const isUser = message.role === 'user'
  const isAssistant = message.role === 'assistant'
  const skillName = message.tool_name
  const [modalOpen, setModalOpen] = useState(false)
  const [previewAttachment, setPreviewAttachment] = useState<AttachmentRef | null>(null)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const saveTriggerRef = useRef<HTMLButtonElement | null>(null)
  const [copiedMd, setCopiedMd] = useState(false)
  const attachments = (message.attachments ?? []) as unknown as AttachmentRef[]
  const presentationOutput = isAssistant ? extractPresentationOutput(message) : null

  return (
    <div className={cn('flex w-full flex-col', isUser ? 'items-end' : 'items-start')}>
      {skillName && isUser && (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="mb-1 inline-flex min-h-11 min-[761px]:min-h-9 items-center gap-1.5 rounded-md bg-accent/10 px-2 py-0.5 text-[12px] text-accent-foreground hover:bg-accent/20"
        >
          <Sparkles className="h-3 w-3" />
          <span className="font-mono">via {skillName}</span>
        </button>
      )}
      <div
        className={cn(
          'min-w-0 rounded-lg px-4 py-2.5',
          isUser
            ? 'max-w-[85%] bg-primary text-primary-foreground'
            : 'w-full border border-border bg-card text-card-foreground',
        )}
      >
        {isAssistant ? (
          <div className="prose prose-sm dark:prose-invert min-w-0 max-w-none prose-headings:font-[650] prose-headings:text-foreground prose-h1:text-[26px] prose-h1:leading-tight prose-h2:text-[16px] prose-h2:leading-snug prose-h3:text-[16px] prose-h3:leading-snug prose-h4:text-[14px] prose-h4:leading-snug prose-p:text-[14px] prose-p:leading-[1.45] prose-p:text-foreground prose-li:text-[14px] prose-li:leading-[1.45] prose-a:text-primary prose-a:underline prose-a:decoration-current/40 prose-a:underline-offset-2 prose-code:font-mono prose-code:text-[13px] prose-pre:overflow-x-auto prose-pre:rounded-md prose-pre:bg-muted prose-pre:p-3 prose-pre:font-mono prose-pre:text-[13px] prose-pre:leading-relaxed prose-strong:text-foreground prose-blockquote:border-l-2 prose-blockquote:border-border prose-blockquote:pl-3 prose-blockquote:text-muted-foreground prose-hr:border-border prose-th:text-left prose-th:font-[650] prose-td:align-top prose-img:max-w-full prose-img:h-auto">
            {presentationOutput ? (
              <PresentationOutputView output={presentationOutput} />
            ) : (
              <MarkdownContent>{message.content}</MarkdownContent>
            )}
            {streaming && <span className="ml-0.5 inline-block h-4 w-1 animate-pulse bg-foreground" />}
          </div>
        ) : (
          <div className="whitespace-pre-wrap text-[14px] leading-[1.45]">{message.content}</div>
        )}

        {isAssistant && !streaming && message.content.trim() && (
          <button
            type="button"
            aria-label={copiedMd ? 'Markdown kopiert' : 'Als Markdown kopieren'}
            title="Als Markdown kopieren"
            onClick={() => {
              void navigator.clipboard.writeText(message.content).then(() => {
                setCopiedMd(true)
                window.setTimeout(() => setCopiedMd(false), 2000)
              })
            }}
            className="mt-1.5 inline-flex h-11 min-[761px]:h-9 items-center gap-1 rounded px-2 text-[12px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {copiedMd ? (
              <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            <span>{copiedMd ? 'Kopiert' : 'Kopieren'}</span>
          </button>
        )}

        {isAssistant && !streaming && message.content.trim() && (
          <ReadAloud message={message} />
        )}

        {isAssistant && message.sources && message.sources.length > 0 && (
          <div data-testid="source-chips" className="mt-2 flex flex-wrap gap-1.5">
            {message.sources.map((source) => (
              <SourceChip key={source.path} source={source} />
            ))}
          </div>
        )}

        {attachments.length > 0 && (
          <div className="mt-2 flex min-w-0 max-w-full flex-col gap-2">
            {attachments.map((a) => {
              const signedUrl = a.signed_url
              if (a.kind === 'image' && signedUrl) {
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setPreviewAttachment(a)}
                    className="block max-w-full overflow-hidden rounded-md border border-border bg-muted/30 transition-opacity hover:opacity-90"
                    title={a.filename}
                  >
                    <img
                      src={signedUrl}
                      alt={a.filename}
                      className="block max-h-[360px] w-full object-contain"
                      loading="lazy"
                    />
                  </button>
                )
              }
              return (
                <AttachmentPill key={a.id} attachment={a} onClick={() => setPreviewAttachment(a)} />
              )
            })}
          </div>
        )}

        {message.tool_calls && message.tool_calls.length > 0 && (
          <ToolCallsView toolCalls={message.tool_calls} />
        )}

        {(message.cost_eur != null || (message.tokens_in != null && message.tokens_out != null)) && (
          <div
            className={cn(
              'mt-2 flex items-center gap-2 text-[12px]',
              isUser ? 'text-primary-foreground/70' : 'text-muted-foreground',
            )}
          >
            {message.cost_eur != null && message.cost_eur > 0 && (
              <span>{eurFormatter.format(message.cost_eur)}</span>
            )}
            {message.tokens_in != null && message.tokens_out != null && (
              <span>
                · {message.tokens_in} → {message.tokens_out} Tokens
              </span>
            )}
          </div>
        )}

        {isAssistant && !streaming && message.content.trim() && (
          <div className="mt-3 flex justify-end border-t border-border pt-2">
            <Button
              type="button"
              ref={saveTriggerRef}
              size="sm"
              variant="ghost"
              className="h-11 px-2 text-[12px] min-[761px]:h-9"
              onClick={() => setSaveDialogOpen(true)}
            >
              Als Dokument speichern
            </Button>
          </div>
        )}
      </div>
      {skillName && isUser && (
        <SkillInvocationModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          sessionId={message.session_id}
          skillName={skillName}
          anchorCreatedAt={message.created_at}
        />
      )}
      <AttachmentPreviewModal
        open={!!previewAttachment}
        onOpenChange={(open) => !open && setPreviewAttachment(null)}
        attachment={previewAttachment}
      />
      <SaveMessageDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        message={message}
        triggerRef={saveTriggerRef}
      />
    </div>
  )
}

/** Kompakter Quellen-Chip (M2-B): deeplinkt auf die Quellen-Seite des Vault-Files. */
function SourceChip({ source }: { source: NonNullable<ChatMessage['sources']>[number] }) {
  const updated = source.updated_at ? formatRelativeDate(source.updated_at) : ''
  return (
    <Link
      to={`/quellen?path=${encodeURIComponent(source.path)}`}
      data-testid="source-chip"
      title={source.path}
      className="inline-flex min-h-11 min-[761px]:min-h-9 max-w-full items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-[12px] text-muted-foreground transition-colors hover:border-primary/50 hover:bg-accent/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <FileText className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" />
      <span className="truncate">{source.title}</span>
      {updated && (
        <span className="shrink-0 text-muted-foreground">· Aktualisiert {updated}</span>
      )}
    </Link>
  )
}

function SaveMessageDialog({
  open,
  onOpenChange,
  message,
  triggerRef,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  message: ChatMessage
  triggerRef: React.RefObject<HTMLButtonElement | null>
}) {
  const putFile = usePutVaultFile()
  const initialFilename = useMemo(() => defaultVaultFilename(message.content, message.created_at), [message])
  const [folder, setFolder] = useState('Notes')
  const [customFolder, setCustomFolder] = useState('')
  const [filename, setFilename] = useState(initialFilename)

  const effectiveFolder = folder === '__custom__' ? customFolder.trim().replace(/^\/+|\/+$/g, '') : folder
  const path = `${effectiveFolder || 'Notes'}/${ensureMarkdownExtension(filename)}`
  const content = markdownForVaultMessage(message)

  // Fokus-Rueckkehr: zum tatsaechlichen Ausloeser, falls noch verbunden und
  // sichtbar; sonst zum naechsten sinnvollen sichtbaren Ersatz im Dokument.
  const resolveAutoFocusTarget = (): HTMLElement | undefined => {
    const trigger = triggerRef.current
    if (trigger?.isConnected && trigger.offsetParent !== null) return trigger
    const panel = document.querySelector<HTMLElement>('main[aria-label="Fragen"]')
    return panel?.isConnected && panel.getClientRects().length > 0 ? panel : undefined
  }

  const save = async () => {
    try {
      await putFile.mutateAsync({
        path,
        content,
        content_type: 'text/markdown',
        frontmatter: {
          type: 'chat_note',
          source: 'chat',
          chat_message_id: message.id,
          chat_session_id: message.session_id,
          created_from_chat_at: new Date().toISOString(),
        },
        tags: ['chat', 'saved-from-chat'],
      })
      toast.success(`Gespeichert nach ${path}`)
      onOpenChange(false)
    } catch (err) {
      // Fehler bleiben sichtbar; Korrektur und Retry sind moeglich.
      toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen')
    }
  }

  const requestClose = (nextOpen: boolean) => {
    if (!nextOpen && putFile.isPending) return
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent
        closeDisabled={putFile.isPending}
        closeLabel="Dialog schliessen"
        onEscapeKeyDown={(event) => {
          if (putFile.isPending) event.preventDefault()
        }}
        onPointerDownOutside={(event) => {
          if (putFile.isPending) event.preventDefault()
        }}
        onCloseAutoFocus={(event) => {
          const target = resolveAutoFocusTarget()
          if (target) {
            event.preventDefault()
            target.focus()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Als Dokument speichern</DialogTitle>
          <DialogDescription className="text-[12px] leading-[1.45]">
            Die Antwort wird als Markdown-Dokument gespeichert. Eine gespeicherte
            KI-Antwort ist damit noch nicht fachlich freigegeben.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor={`vault-folder-${message.id}`}>Ordner</Label>
            <select
              id={`vault-folder-${message.id}`}
              value={folder}
              onChange={(event) => setFolder(event.target.value)}
              className="flex h-11 min-[761px]:h-9 w-full rounded-md border border-input bg-background px-3 text-[16px] min-[761px]:text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="Notes">Notes/</option>
              <option value="_Knowledge">_Knowledge/</option>
              <option value="_Research">_Research/</option>
              <option value="_Research/Meetings">_Research/Meetings/</option>
              <option value="__custom__">Custom...</option>
            </select>
          </div>
          {folder === '__custom__' && (
            <div className="grid gap-2">
              <Label htmlFor={`vault-custom-folder-${message.id}`}>Custom-Ordner</Label>
              <Input
                id={`vault-custom-folder-${message.id}`}
                value={customFolder}
                onChange={(event) => setCustomFolder(event.target.value)}
                placeholder="Notes/Meetings"
              />
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor={`vault-filename-${message.id}`}>Dateiname</Label>
            <Input
              id={`vault-filename-${message.id}`}
              value={filename}
              onChange={(event) => setFilename(event.target.value)}
            />
          </div>
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-[12px] text-muted-foreground break-all">
            {path}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => requestClose(false)} disabled={putFile.isPending} className="min-h-11 min-[761px]:min-h-9">
            Abbrechen
          </Button>
          <Button type="button" onClick={() => void save()} disabled={putFile.isPending || !filename.trim()} className="min-h-11 min-[761px]:min-h-9">
            {putFile.isPending ? 'Speichert...' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function markdownForVaultMessage(message: ChatMessage): string {
  const body = message.content
    .replace(/```mindkeep-presentation\s*[\s\S]*?\s*```/gi, '')
    .trim()
  const createdAt = new Date(message.created_at).toISOString()
  return [
    '---',
    'type: chat_note',
    'source: chat',
    `chat_message_id: ${JSON.stringify(message.id)}`,
    `chat_session_id: ${JSON.stringify(message.session_id)}`,
    `created_from_chat_at: ${JSON.stringify(createdAt)}`,
    '---',
    '',
    body || message.content.trim(),
    '',
  ].join('\n')
}

function defaultVaultFilename(content: string, createdAt: string): string {
  const date = new Date(createdAt)
  const day = Number.isNaN(date.getTime()) ? new Date().toISOString().slice(0, 10) : date.toISOString().slice(0, 10)
  const firstLine = content
    .replace(/```mindkeep-presentation\s*[\s\S]*?\s*```/gi, '')
    .split('\n')
    .find((line) => line.trim().length > 0) ?? 'chat-notiz'
  return `${day}-${slugifyFilename(firstLine, 56)}.md`
}

function ensureMarkdownExtension(value: string): string {
  const clean = sanitizePathSegment(value.trim() || 'chat-notiz')
  return clean.toLowerCase().endsWith('.md') ? clean : `${clean}.md`
}

function sanitizePathSegment(value: string): string {
  return value
    .replace(/\\/g, '/')
    .split('/')
    .filter(Boolean)
    .map((segment) => slugifyFilename(segment, 80))
    .join('/')
    || 'chat-notiz.md'
}

function slugifyFilename(value: string, maxLength: number): string {
  const normalized = value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return (normalized || 'chat-notiz').slice(0, maxLength).replace(/-+$/g, '') || 'chat-notiz'
}

interface PresentationOutput {
  htmlUrl?: string
  pdfUrl?: string
  slideCount?: number
  aspect?: string
  rawMarkdown?: string
}

function PresentationOutputView({ output }: { output: PresentationOutput }) {
  const [showMarkdown, setShowMarkdown] = useState(false)
  const [copied, setCopied] = useState(false)
  const token = useOutputAuthToken()
  const htmlUrl = authenticatedOutputUrl(output.htmlUrl, token)
  const pdfUrl = authenticatedOutputUrl(output.pdfUrl, token)
  const meta = [
    'Präsentation',
    typeof output.slideCount === 'number' ? `${output.slideCount} Folien` : null,
    output.aspect,
  ].filter(Boolean).join(' · ')

  return (
    <div className="presentation-output not-prose space-y-3 rounded-md border border-border bg-muted/20 p-3">
      <div className="output-meta text-sm font-medium text-foreground">{meta}</div>
      <div className="output-actions flex flex-wrap gap-2">
        {htmlUrl && (
          <Button asChild size="sm" variant="outline" className="btn-download btn-html">
            <a href={htmlUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              HTML öffnen
            </a>
          </Button>
        )}
        {pdfUrl && (
          <Button asChild size="sm" className="btn-download btn-pdf">
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" download>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              PDF herunterladen
            </a>
          </Button>
        )}
        {output.rawMarkdown && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowMarkdown((value) => !value)}
          >
            {showMarkdown ? <ChevronDown className="mr-1.5 h-3.5 w-3.5" /> : <ChevronRight className="mr-1.5 h-3.5 w-3.5" />}
            Inhalt anzeigen
          </Button>
        )}
      </div>
      {output.rawMarkdown && showMarkdown && (
        <div className="rounded border border-border bg-background">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Markdown
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-11 min-[761px]:h-9 gap-1 px-2 text-[11px]"
              onClick={() => {
                void navigator.clipboard.writeText(output.rawMarkdown ?? '').then(() => {
                  setCopied(true)
                  window.setTimeout(() => setCopied(false), 1500)
                })
              }}
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              MD kopieren
            </Button>
          </div>
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap p-3 font-mono text-[12px] leading-relaxed text-foreground">
            {output.rawMarkdown}
          </pre>
        </div>
      )}
    </div>
  )
}

function extractPresentationOutput(message: ChatMessage): PresentationOutput | null {
  const looseMessage = message as ChatMessage & {
    metadata?: unknown
    outputResult?: unknown
    json_payload?: unknown
  }
  const candidates = [
    looseMessage.metadata,
    looseMessage.outputResult,
    looseMessage.json_payload,
    parseEmbeddedPresentationPayload(message.content),
    parseJsonObject(message.content),
    parsePresentationLinks(message.content),
  ]

  for (const candidate of candidates) {
    const output = normalizePresentationOutput(candidate, message.workspace_id)
    if (output) return output
  }
  return null
}

function normalizePresentationOutput(value: unknown, workspaceId: string): PresentationOutput | null {
  if (!isRecord(value)) return null

  const nestedKeys = ['metadata', 'outputResult', 'json_payload', 'jsonPayload', 'payload', 'result']
  for (const key of nestedKeys) {
    const nested = normalizePresentationOutput(value[key], workspaceId)
    if (nested) return nested
  }

  const htmlUrl = readString(value, ['html_url', 'htmlUrl', 'html_signed_url', 'htmlSignedUrl'])
    ?? renderUrlFromVaultPath(workspaceId, readPlainString(value, ['html_vault_path', 'htmlVaultPath']))
  const pdfUrl = readString(value, ['pdf_url', 'pdfUrl', 'pdf_signed_url', 'pdfSignedUrl'])
    ?? renderUrlFromVaultPath(workspaceId, readPlainString(value, ['pdf_vault_path', 'pdfVaultPath']))
  if (!htmlUrl && !pdfUrl) return null

  return {
    htmlUrl,
    pdfUrl,
    slideCount: readNumber(value, ['slide_count', 'slideCount']),
    aspect: readPlainString(value, ['aspect', 'aspect_ratio', 'aspectRatio']),
    rawMarkdown: readPlainString(value, ['raw_markdown', 'rawMarkdown', 'markdown']),
  }
}

function parseEmbeddedPresentationPayload(content: string): unknown {
  const match = content.match(/```mindkeep-presentation\s*([\s\S]*?)\s*```/i)
  if (!match?.[1]) return null
  try {
    return JSON.parse(match[1]) as unknown
  } catch {
    return null
  }
}

function parseJsonObject(content: string): unknown {
  const trimmed = content.trim()
  if (!trimmed) return null

  const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i)
  const raw = fenced?.[1]?.trim() ?? trimmed
  if (!raw.startsWith('{') || !raw.endsWith('}')) return null

  try {
    return JSON.parse(raw) as unknown
  } catch {
    return null
  }
}

function parsePresentationLinks(content: string): PresentationOutput | null {
  const htmlUrl = findLabeledUrl(content, 'HTML')
  const pdfUrl = findLabeledUrl(content, 'PDF')
  if (!htmlUrl && !pdfUrl) return null
  const slideCountMatch = content.match(/(\d+)\s+Folien/i)
  const aspectMatch = content.match(/\b(16:9|4:5|1:1)\b/)
  return {
    htmlUrl,
    pdfUrl,
    slideCount: slideCountMatch?.[1] ? Number.parseInt(slideCountMatch[1], 10) : undefined,
    aspect: aspectMatch?.[1],
  }
}

function findLabeledUrl(content: string, label: 'HTML' | 'PDF'): string | undefined {
  const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const urlPattern = `(?:https?:\\/\\/\\S+|\\/api\\/outputs\\/render\\?\\S+)`
  const plain = content.match(new RegExp(`${escapedLabel}:\\s*(${urlPattern})`, 'i'))?.[1]
  if (plain) return plain.replace(/[),.;]+$/, '')
  const markdown = content.match(new RegExp(`\\[[^\\]]*${escapedLabel}[^\\]]*\\]\\((${urlPattern.replace('\\S+', '[^)]+')})\\)`, 'i'))?.[1]
  return markdown?.replace(/[),.;]+$/, '')
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function readString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'string' && (/^https?:\/\//i.test(value) || value.startsWith('/api/'))) return value
  }
  return undefined
}

function renderUrlFromVaultPath(workspaceId: string, vaultPath: string | undefined): string | undefined {
  if (!vaultPath) return undefined
  if (!/^_Outputs\//.test(vaultPath) || vaultPath.includes('..')) return undefined
  return `/api/outputs/render?path=${encodeURIComponent(`${workspaceId}/${vaultPath}`)}`
}

function readPlainString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'string' && value.trim()) return value
  }
  return undefined
}

function readNumber(obj: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = obj[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && /^\d+$/.test(value)) return Number.parseInt(value, 10)
  }
  return undefined
}

interface ToolCall {
  id: string
  type?: string
  function?: { name?: string; arguments?: string | Record<string, unknown> }
}

function ToolCallsView({ toolCalls }: { toolCalls: ToolCall[] }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="mt-3 rounded-md border border-border bg-muted/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-muted-foreground hover:bg-muted/70"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span className="font-mono">
          {toolCalls.map((tc) => tc.function?.name ?? 'tool').join(', ')}
        </span>
      </button>
      {open && (
        <pre className="overflow-auto border-t border-border px-3 py-2 font-mono text-[12px] leading-relaxed">
          {JSON.stringify(toolCalls, null, 2)}
        </pre>
      )}
    </div>
  )
}
